/**
 * voice-v8 — HTTP-Chat-Client (Phase H).
 *
 * Streamt eine Bot-Antwort vom Server via Server-Sent Events. Der
 * Server (siehe server/chat.ts) ruft Anthropic Claude direkt auf —
 * KEIN LiveKit, KEIN TTS. Reiner Text-Modus.
 */

import { merkeSchmiedFolgt, merkeZug, zugBeginnt, zugZuEnde } from "./zug-ende.js";
import { meldeSchmiedAngesprochen } from "./schmied-warten.js";
import { meldeZusatzAngesprochen } from "./adressat-vermerk.js";
import { istZusatzId } from "../../shared/entwickler.js";
import type { MartinsEmpfaenger } from "../../shared/dreier-adressat.js";

interface ToolCallEvent {
  id: string;
  name: string;
  input: Record<string, unknown>;
}

interface ToolResultEvent {
  id: string;
  name: string;
  result: unknown;
  /** 15.10.2026: die Anzeige des Servers (server/werkzeug-anzeige.ts), ungeprüft. */
  anzeige?: unknown;
}

interface ToolProgressEvent {
  tool: string;
  callId?: string;
  phase: "started" | "running" | "done";
  elapsedMs?: number;
  lastLine?: string;
  /** Musik (Ticket 3074): die Stufe des Stücks — gelesen mit `fortschrittAus` (shared/lied-fortschritt.ts). */
  fortschritt?: unknown;
}

export interface ChatStreamOptions {
  message: string;
  /**
   * ⚠️ Altbestand, EINZAHL. Bleibt vorerst, weil die native Huelle im
   * WKWebView eine aeltere Frontend-Fassung ausliefern kann — die schickt
   * weiterhin dieses Feld. Der Server nimmt beide an.
   */
  imageUrl?: string;
  /** 2026-08-28: bis zu fuenf Bilder in EINEM Zug. */
  imageUrls?: string[];
  caption?: string;
  /**
   * 23.09.2026, Ticket 2498: der Zug eines Datei-Vorgangs. Nimmt ihn am
   * Server vom Beleg-Weg aus (istBelegUpload in server/chat.ts).
   */
  vorgang?: boolean;
  /**
   * 16.10.2026 (Dreier-Modus Stufe 1, lib/empfaenger-wahl.ts): Martins Wahl „An: Schmied“ oder sein Antippen von „Vespera“ (holt
   * sie aus dem Faden zum Schmied zurück) — nur dann gesetzt; ohne Feld entscheidet der Server aus Anrede und Faden
   * (server/dreier-modus.ts, `nachrichtAnSchmied`).
   */
  an?: MartinsEmpfaenger;
  /** 20.10.2026: die Nachricht ist Martins Tipp auf Ja/Nein einer Karte (Server prüft: nur „Ja“/„Nein“ an den Schmied) — der Chat zeigt keine eigene Blase. */
  kartenantwort?: true;
  /** 27.10.2026 (Mandat, server/prozess-freigabe.ts): die Nachricht kommt vom Knopf einer Karte (`sendUserMessageFromCard`), nie aus dem Eingabefeld. */
  vonKarte?: true;
  signal?: AbortSignal;
  /**
   * 11.10.2026, Ticket 2845: der Server hat die Anfrage mit 200 angenommen — ab hier rechnet er, auch wenn die
   * Leitung reißt oder eine Anzeige wirft (server/chat.ts, Ticket 2256). Alles DAVOR (offline, HTTP-Fehler) heißt:
   * nicht angekommen. Wer wegen eines Fehlers etwas zurücklegt oder erneut sendet, fragt hiernach.
   */
  onAngenommen?: () => void;
  onChunk: (chunk: string) => void;
  onToolCall?: (call: ToolCallEvent) => void;
  onToolResult?: (result: ToolResultEvent) => void;
  /**
   * Pre.355: Live-Progress während lang laufender Tools (claude_task_server,
   * analyze_video, narrate_to_mp3). Frontend rendert daraus eine Spinner-
   * Bubble (Pre.339).
   */
  onToolProgress?: (progress: ToolProgressEvent) => void;
  onError?: (error: string) => void;
  /**
   * Der Zug ist zu Ende — auf JEDEM Weg: fertig, Fehler, Stille (Watchdog), Abbruch durch den Nutzer, Netz weg. (Ticket 3074) Hier räumt der
   * Aufrufer weg, was nur während des Stroms galt (die Stufenkarte der Musik): ohne „fertig“ vom Server liefe ihre Uhr sonst ewig weiter.
   */
  onEnde?: () => void;
  /**
   * Pre.421: Server schickt im "done"-Event das tatsächlich genutzte LLM.
   * Bei Anthropic-Credit-Empty fällt Server auf Gemini Lite / OpenAI;
   * Footer aktualisiert sich daraufhin damit User die Realität sieht.
   */
  /** Ticket 281: fruehes Modell-Event — Footer zeigt sofort das echte Modell. */
  onModel?: (effectiveModel: string) => void;
  /**
   * 29.09.2026, Ticket 2790: Am Zugende wartet eine Fragen-Runde auf seine
   * Antwort (tools/tickets.ts, rundeWartet). Kommt vor `onDone`.
   */
  onRunde?: (wartet: boolean) => void;
  onDone?: (info: { effectiveModel?: string }) => void;
  /**
   * Pre.461: TruthCheck-Warning. Server-Side Regex hat im Reply eine
   * bekannte Lügen-Phrase erkannt (Browser-Einstellungen, Sync-Verzögerung,
   * etc.). Frontend rendert ein Warn-Badge an der Bot-Bubble.
   */
  onTruthWarning?: (warn: {
    severity: "warn" | "info" | "none";
    matches: string[];
  }) => void;
  /** Ticket #458: Server schlaegt ein thematisch passenderes Projekt vor. */
  onProjectSuggest?: (s: {
    projectId: string;
    name: string;
    score: number;
  }) => void;
}

/**
 * Ticket 405: Inaktivitaets-Watchdog. Auf Mobil stirbt eine Verbindung gern
 * "halb offen" (kein FIN) — `reader.read()` bleibt dann fuer immer pending,
 * `streamChat` settelt NIE, und der Aufrufer haengt in seinem "arbeitet
 * gerade"-Zustand fest (Senden-Knopf bleibt Stop-Knopf, jeder weitere Klick
 * wird verschluckt). Vorbild: chat-stream.ts (SSE-Watchdog, gleicher Grund).
 * Der Server schickt alle 25s `event: ping` — 60s Stille = 2 verpasste Pings.
 */
const STALL_TIMEOUT_MS = 60_000;
const STALL_CHECK_MS = 10_000;

/**
 * Ticket 405: Der Stream wurde vom Watchdog abgebrochen. KEIN verlorener Turn —
 * der Server rechnet weiter und liefert die Antwort ueber /api/chat/stream nach.
 * Der Aufrufer soll das ehrlich anzeigen statt still zu schlucken.
 */
export const CHAT_STREAM_STALLED = "stream-stalled";

export async function streamChat(opts: ChatStreamOptions): Promise<void> {
  // Welle A (15.10.2026, lib/zug-ende.ts): dieser Strom besitzt seinen Zug — das Mikro entscheidet erst nach seinem Ende.
  const zugKennung = zugBeginnt();
  // Eigener Controller: reicht ein externes signal weiter UND kann vom
  // Watchdog ausgeloest werden, um den toten Socket freizugeben.
  const controller = new AbortController();
  const onOuterAbort = (): void => controller.abort();
  if (opts.signal) {
    if (opts.signal.aborted) controller.abort();
    else opts.signal.addEventListener("abort", onOuterAbort, { once: true });
  }

  let lastActivityAt = Date.now();
  let stalled = false;
  let markStalled: (() => void) | null = null;
  // Rennt gegen fetch/reader.read(): lehnt ab, sobald der Watchdog anschlaegt.
  // Damit settelt streamChat GARANTIERT — auch wenn der Socket weder Daten
  // noch Fehler liefert und ein abort() dort nichts mehr aufweckt.
  const stallRace = new Promise<never>((_resolve, reject) => {
    markStalled = (): void => {
      stalled = true;
      reject(new Error(CHAT_STREAM_STALLED));
    };
  });
  stallRace.catch(() => {
    /* kein unhandled rejection, falls niemand mehr liest */
  });
  // Interval statt einmaligem Timeout: nach einer Hintergrundphase (Timer
  // eingefroren) wird beim Aufwachen gegen die echte Uhr geprueft — die
  // Oberflaeche ist damit sofort nach der Rueckkehr wieder bedienbar.
  const watchdog = setInterval(() => {
    if (Date.now() - lastActivityAt <= STALL_TIMEOUT_MS) return;
    controller.abort();
    markStalled?.();
  }, STALL_CHECK_MS);

  try {
    await streamChatOnce(opts, controller.signal, stallRace, () => {
      lastActivityAt = Date.now();
    }, zugKennung);
  } catch (err) {
    if (stalled) {
      opts.onError?.(CHAT_STREAM_STALLED);
      return;
    }
    throw err;
  } finally {
    zugZuEnde(zugKennung); // jedes Ende (auch Abriss, Wachhund) — danach darf das Vorlese-Ende wieder entscheiden
    clearInterval(watchdog);
    opts.signal?.removeEventListener("abort", onOuterAbort);
    try {
      opts.onEnde?.();
    } catch {
      /* das Aufräumen darf den Ausgang des Zugs nie ändern */
    }
  }
}

async function streamChatOnce(
  opts: ChatStreamOptions,
  signal: AbortSignal,
  stallRace: Promise<never>,
  onActivity: () => void,
  zugKennung: number,
): Promise<void> {
  const res = await Promise.race([
    fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        message: opts.message,
        imageUrl: opts.imageUrl,
        imageUrls: opts.imageUrls,
        caption: opts.caption,
        vorgang: opts.vorgang,
        ...(opts.an ? { an: opts.an } : {}),
        ...(opts.kartenantwort ? { kartenantwort: true } : {}),
        ...(opts.vonKarte ? { vonKarte: true } : {}),
      }),
      signal,
    }),
    stallRace,
  ]);
  if (!res.ok || !res.body) {
    opts.onError?.(`http-${res.status}`);
    return;
  }
  // Ticket 2845: ab hier ist die Nachricht beim Server. Ein Fehler der Anzeige darf das nicht widerrufen.
  try {
    opts.onAngenommen?.();
  } catch {
    /* eine Meldung über die Annahme darf den Zug nie kosten */
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  // 2026-07-22 (Forensik 21.07., stummer Vormittag): merken, ob je ein
  // Terminal-Event (done/error) bzw. Text ankam — endet der Stream ohne
  // beides, darf streamChat NICHT still zurueckkehren.
  let sawTerminal = false;
  let sawText = false;
  for (;;) {
    // Ticket 405: jeder Lesevorgang (auch ein `event: ping`) zaehlt als
    // Lebenszeichen und setzt den Watchdog zurueck.
    const { value, done } = await Promise.race([reader.read(), stallRace]);
    onActivity();
    if (done) break;
    buffer += value;
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const event = parseSseBlock(block);
      if (!event) continue;
      if (event.event === "text" && typeof event.data?.chunk === "string") {
        sawText = true;
        opts.onChunk(event.data.chunk);
      } else if (event.event === "tool_call") {
        opts.onToolCall?.(event.data as unknown as ToolCallEvent);
      } else if (event.event === "tool_result") {
        opts.onToolResult?.(event.data as unknown as ToolResultEvent);
      } else if (event.event === "tool_progress") {
        opts.onToolProgress?.(event.data as unknown as ToolProgressEvent);
      } else if (
        event.event === "error" &&
        typeof event.data?.error === "string"
      ) {
        sawTerminal = true;
        opts.onError?.(event.data.error);
      } else if (event.event === "project_suggest") {
        const d = event.data as unknown as {
          projectId?: string;
          name?: string;
          score?: number;
        };
        if (d?.projectId && d?.name) {
          opts.onProjectSuggest?.({
            projectId: String(d.projectId),
            name: String(d.name),
            score: Number(d.score ?? 0),
          });
        }
      } else if (event.event === "truth-warning") {
        opts.onTruthWarning?.({
          severity:
            event.data?.severity === "warn" ||
            event.data?.severity === "info" ||
            event.data?.severity === "none"
              ? event.data.severity
              : "warn",
          matches: Array.isArray(event.data?.matches)
            ? (event.data.matches as string[])
            : [],
        });
      } else if (event.event === "model") {
        if (typeof event.data?.effectiveModel === "string") {
          opts.onModel?.(event.data.effectiveModel);
        }
      } else if (event.event === "runde") {
        opts.onRunde?.(event.data?.wartet === true);
      } else if (event.event === "schmied") {
        // 15.10.2026: Martin hat den Schmied angesprochen — Vespera antwortet nicht, das Mikro wartet auf ihn (lib/schmied-warten.ts).
        meldeSchmiedAngesprochen(event.data);
      } else if (istZusatzId(event.event)) {
        // 17.10.2026: Martin hat den Zimmermann (oder den Hüter) angesprochen — Vespera antwortet nicht, nichts wartet (kein Mikro-Warten).
        meldeZusatzAngesprochen(event.event);
      } else if (event.event === "schmied-folgt") {
        // 16.10.2026: Martin sprach beide an, der Schmied antwortet nach Vespera — das Mikro wartet dann auf ihn.
        merkeSchmiedFolgt(zugKennung);
      } else if (event.event === "zug") {
        // Welle A: was der Zugvertrag meldet (Arbeits- oder Plauderzug, echte Rückfrage) — vor `done`.
        merkeZug(zugKennung, event.data);
      } else if (event.event === "done") {
        sawTerminal = true;
        // VOR onDone: wer danach vorliest, fragt am Ende, ob der Zug fertig ist.
        zugZuEnde(zugKennung);
        opts.onDone?.({
          effectiveModel:
            typeof event.data?.effectiveModel === "string"
              ? event.data.effectiveModel
              : undefined,
        });
        return;
      }
    }
  }
  // Stream regulaer geschlossen, aber OHNE done/error-Event: der Server ist
  // zwischen Header und erstem Event ausgestiegen. Teilantwort behalten,
  // Leere ehrlich melden — nie wieder eine stumme "..."-Blase.
  if (!sawTerminal) {
    if (sawText) {
      opts.onDone?.({});
    } else {
      // 2026-09-04 (Martin: „ich mache die App nur klein, dann heißt es, es
      // kam zu einer Unterbrechung"): Der häufigste Grund für einen Strom
      // ohne Abschluss ist kein Serverfehler, sondern ein Wechsel in den
      // Hintergrund — iOS kappt dann den Kanal. Der Server schreibt die
      // Antwort zu Ende und legt sie ab; die App holt sie beim Zurückkommen
      // nach (siehe visibilitychange in app.ts).
      //
      // ⚠️ „Bitte nochmal senden" war deshalb ein falscher Rat: Er erzeugt
      //    eine zweite Antwort auf dieselbe Frage.
      //
      // ⚠️ KEIN eigener visibilitychange-Wächter hier. Er müsste bei jedem
      //    Ausstieg wieder abgemeldet werden, und ein vergessener Wächter je
      //    Chat-Zug ist genau die Art Leck, die eine App über Stunden zäh
      //    macht. Ist der Nutzer beim Auswerten schon zurück, holt das
      //    Nachladen in app.ts die Antwort ohnehin — die Meldung ist dann
      //    nur ein Wimpernschlag lang zu sehen.
      opts.onError?.(
        document.hidden
          ? "Die Antwort wird im Hintergrund fertig geschrieben — sie erscheint hier, sobald du zurück bist."
          : "Der Server hat die Verbindung ohne Antwort geschlossen — bitte nochmal senden.",
      );
    }
  }
}

function parseSseBlock(
  block: string,
): { event: string; data: Record<string, unknown> } | null {
  const lines = block.split("\n");
  let eventName = "message";
  let dataStr = "";
  for (const line of lines) {
    if (line.startsWith("event:")) eventName = line.slice(6).trim();
    else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
  }
  if (!dataStr) return null;
  try {
    return { event: eventName, data: JSON.parse(dataStr) };
  } catch {
    return null;
  }
}
